import { listChannels } from '@/features/channels/api';
import { listDepartments, listTemplates, listUsers } from '@/features/configuracoes/api';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Loader2, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { TemplateBuilder } from './TemplateBuilder';
import {
  type CreateMessageEventInput,
  type MessageEvent,
  type MessageEventAction,
  type MessageEventVariables,
  buildTriggerUrl,
  createMessageEvent,
  extractJsonPaths,
  updateMessageEvent,
} from './api';

interface Props {
  event?: MessageEvent;
  onClose: () => void;
}

const ACTION_OPTIONS: Array<{ value: MessageEventAction; label: string }> = [
  { value: 'no_ticket', label: 'Não criar' },
  { value: 'close', label: 'Fechar' },
  { value: 'keep_open', label: 'Manter aberto' },
  { value: 'queue', label: 'Direcionar para departamento' },
  { value: 'assign_user', label: 'Direcionar para usuário' },
];

export function MessageEventEditor({ event, onClose }: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const isEdit = !!event;

  const [name, setName] = useState(event?.name ?? '');
  const [channelId, setChannelId] = useState<number | ''>(event?.channel_id ?? '');
  const [dataModelText, setDataModelText] = useState<string>(
    event
      ? JSON.stringify(event.data_model, null, 2)
      : '{\n  "phone": "5511999999999",\n  "name": "João"\n}',
  );
  const [messageBody, setMessageBody] = useState<string>(event?.message_body ?? '');
  const [templateId, setTemplateId] = useState<number | null>(event?.template_id ?? null);
  const [templateVariables, setTemplateVariables] = useState<MessageEventVariables>(
    event?.template_variables ?? {},
  );
  const [leadMediaPath, setLeadMediaPath] = useState(event?.lead_media_path ?? '');
  const [actionAfter, setActionAfter] = useState<MessageEventAction>(
    event?.action_after ?? 'close',
  );
  const [targetUserId, setTargetUserId] = useState<number | ''>(event?.action_target_user_id ?? '');
  const [targetDeptId, setTargetDeptId] = useState<number | ''>(event?.action_target_dept_id ?? '');
  const [leadPhonePath, setLeadPhonePath] = useState(event?.lead_phone_path ?? '');
  const [leadNamePath, setLeadNamePath] = useState(event?.lead_name_path ?? '');

  const [error, setError] = useState<string | null>(null);
  const [createdTriggerPath, setCreatedTriggerPath] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<'url' | 'curl' | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Parse JSON do data_model pra extrair paths (silencioso se inválido)
  const { parsedDataModel, parseError, jsonPaths } = useMemo(() => {
    try {
      const parsed = JSON.parse(dataModelText || '{}');
      return {
        parsedDataModel: parsed,
        parseError: null as string | null,
        jsonPaths: extractJsonPaths(parsed),
      };
    } catch (err) {
      return {
        parsedDataModel: null,
        parseError: err instanceof Error ? err.message : 'JSON inválido',
        jsonPaths: [],
      };
    }
  }, [dataModelText]);

  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => listUsers() });
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const templatesQ = useQuery({
    queryKey: ['templates', 'approved'],
    queryFn: () => listTemplates('approved'),
  });

  // Detecta tipo do canal selecionado pra bifurcar texto livre vs template HSM
  const selectedChannel = useMemo(
    () => (channelsQ.data ?? []).find((c) => c.id === Number(channelId)),
    [channelsQ.data, channelId],
  );
  const isCloudApi = selectedChannel?.channelType === 'meta_official';

  // Templates filtrados pelo account_id do canal selecionado
  const channelTemplates = useMemo(() => {
    if (!isCloudApi || !selectedChannel || !templatesQ.data) return [];
    return templatesQ.data.filter((t) => t.whatsappAccountId === selectedChannel.accountId);
  }, [isCloudApi, selectedChannel, templatesQ.data]);

  // Quando troca de canal pra QR, limpa template (não faz sentido)
  // Quando troca pra Cloud API, limpa messageBody (vai pro modo template)
  // Mas só se NÃO é edição de evento existente (preserva valores salvos)
  // — controlado pelo useEffect abaixo. Sem useEffect: depende do user limpar manual.

  const buildInput = (): CreateMessageEventInput | null => {
    if (!name.trim()) {
      setError('Defina um nome.');
      return null;
    }
    if (!channelId) {
      setError('Selecione um canal.');
      return null;
    }
    if (parseError) {
      setError(`Modelo de Dados inválido: ${parseError}`);
      return null;
    }
    if (!leadPhonePath.trim()) {
      setError('Defina o caminho do Modelo de número (ex: "phone").');
      return null;
    }
    // Cloud API → exige template
    if (isCloudApi) {
      if (!templateId) {
        setError('Selecione um template HSM (obrigatório para canais Cloud API).');
        return null;
      }
    } else {
      // QR code → exige mensagem OU arquivo (lead_media_path)
      if (!messageBody.trim() && !leadMediaPath) {
        setError('Digite a mensagem OU configure o campo "Arquivo do lead" (pelo menos um).');
        return null;
      }
    }
    if (actionAfter === 'queue' && !targetDeptId) {
      setError('Selecione um departamento pra "Direcionar para departamento".');
      return null;
    }
    if (actionAfter === 'assign_user' && !targetUserId) {
      setError('Selecione um usuário pra "Direcionar para usuário".');
      return null;
    }
    setError(null);
    return {
      name: name.trim(),
      channel_id: Number(channelId),
      data_model: parsedDataModel ?? {},
      message_body: isCloudApi ? null : messageBody || null,
      template_id: isCloudApi ? templateId : null,
      template_variables: isCloudApi ? templateVariables : {},
      action_after: actionAfter,
      action_target_user_id: actionAfter === 'assign_user' ? Number(targetUserId) : null,
      action_target_dept_id: actionAfter === 'queue' ? Number(targetDeptId) : null,
      lead_phone_path: leadPhonePath.trim(),
      lead_name_path: leadNamePath.trim() || null,
      lead_media_path: leadMediaPath.trim() || null,
    };
  };

  const createMut = useMutation({
    mutationFn: (input: CreateMessageEventInput) => createMessageEvent(input),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['message-events'] });
      setCreatedTriggerPath(res.trigger_url ?? res.trigger_path);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : String(err)),
  });

  const updateMut = useMutation({
    mutationFn: (input: CreateMessageEventInput) => updateMessageEvent(event!.id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-events'] });
      toast.success('Evento atualizado');
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : String(err)),
  });

  const submitting = createMut.isPending || updateMut.isPending;

  const handleSubmit = () => {
    const input = buildInput();
    if (!input) return;
    if (isEdit) updateMut.mutate(input);
    else createMut.mutate(input);
  };

  const insertVariable = (path: string) => {
    const ta = messageRef.current;
    if (!ta) return;
    const insertion = `{{${path}}}`;
    const start = ta.selectionStart ?? messageBody.length;
    const end = ta.selectionEnd ?? messageBody.length;
    const next = messageBody.slice(0, start) + insertion + messageBody.slice(end);
    setMessageBody(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insertion.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const copyToClipboard = async (value: string, field: 'url' | 'curl') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('Falha ao copiar');
    }
  };

  // Tela pós-create — mostra URL + curl pronto
  if (createdTriggerPath) {
    const url = buildTriggerUrl(createdTriggerPath);
    const curl = `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(parsedDataModel ?? {})}'`;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="w-full max-w-2xl rounded-md bg-n-0 p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold text-[18px] text-n-900">Webhook criado</h3>
            <button type="button" onClick={onClose} className="text-n-500 hover:text-n-700">
              <X size={18} />
            </button>
          </div>

          <div className="rounded-md bg-brand-50 border border-brand-200 p-3 text-[12px] text-brand-700">
            ℹ️ Aponte seu sistema externo pra esta URL com método <strong>POST</strong> +
            <strong> Content-Type: application/json</strong>. Não é necessário token.
          </div>

          <div className="mt-4 space-y-3">
            <Field
              label="URL de trigger"
              value={url}
              onCopy={() => copyToClipboard(url, 'url')}
              copied={copiedField === 'url'}
              mono
            />
            <div>
              <div className="mb-1 flex items-center justify-between text-[12px] text-n-600">
                <span className="font-semibold">Exemplo curl</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(curl, 'curl')}
                  className="flex items-center gap-1 text-brand-600 hover:underline"
                >
                  {copiedField === 'curl' ? <Check size={12} /> : <Copy size={12} />}
                  Copiar
                </button>
              </div>
              <pre className="overflow-x-auto rounded border border-n-200 bg-n-50 p-3 font-mono text-[11px] text-n-700">
                {curl}
              </pre>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={onClose}>Concluir</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-md bg-n-0 shadow-xl">
        <header className="flex items-center justify-between border-b border-n-200 px-6 py-4">
          <div>
            <h3 className="font-bold text-[18px] text-n-900">
              {isEdit ? 'Editar Evento de Mensagem' : 'Criar Evento de Mensagem'}
            </h3>
            <p className="text-[12px] text-n-500">
              Configure eventos disparados por sistemas externos via webhook.
              {!selectedChannel && <> Comece selecionando um canal abaixo.</>}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-n-500 hover:text-n-700">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* ───── Dados Evento ───── */}
          <section className="rounded-md border border-n-200 p-4">
            <div className="mb-3 font-semibold text-[13px] text-n-700">Dados Evento</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-[12px] text-n-600">Nome</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Evento CRM Acme"
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] text-n-600">Envio por</label>
                <select
                  value={channelId}
                  onChange={(e) => setChannelId(e.target.value ? Number(e.target.value) : '')}
                  className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-3 text-sm"
                >
                  <option value="">Selecione um canal…</option>
                  {/* Instagram fora: evento manda pra um telefone (= iniciar
                      conversa), o que o Instagram não permite. */}
                  {(channelsQ.data ?? [])
                    .filter((c) => c.channelType !== 'instagram')
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName ?? c.displayNumber ?? `Canal #${c.id}`} (
                        {c.channelType === 'qr_code' ? 'QR' : 'Cloud API'})
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </section>

          {selectedChannel && (
            <>
              {/* ───── Modelo de Dados ───── */}
              <section className="mt-4 rounded-md border border-n-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[13px] text-n-700">
                      Modelo de Dados (JSON)
                    </div>
                    <p className="text-[11px] text-n-500">
                      Cole o body de um evento anterior pra extrair variáveis. Aceito como template
                      visual — o sistema só usa pra te mostrar os campos disponíveis.
                    </p>
                  </div>
                  {jsonPaths.length > 0 && (
                    <span className="rounded bg-success-50 px-2 py-1 text-[11px] text-success-700">
                      {jsonPaths.length} variável{jsonPaths.length === 1 ? '' : 'is'} detectada
                      {jsonPaths.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <textarea
                  value={dataModelText}
                  onChange={(e) => setDataModelText(e.target.value)}
                  spellCheck={false}
                  className={`h-32 w-full rounded-md border bg-n-50 p-3 font-mono text-[12px] text-n-700 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25 ${
                    parseError ? 'border-danger-300' : 'border-n-300'
                  }`}
                  placeholder='{"phone": "5511...", "name": "..."}'
                />
                {parseError && (
                  <p className="mt-1 text-[11px] text-danger-500">JSON inválido: {parseError}</p>
                )}
              </section>

              {/* ───── Aba ENVIO ───── */}
              <section className="mt-4 rounded-md border border-n-200 p-4">
                <div className="mb-3 border-b border-brand-500 pb-2 font-semibold text-[13px] text-brand-600">
                  ➤ ENVIO
                </div>

                <div>
                  <label className="mb-2 block text-[12px] text-n-600">
                    Ação no atendimento após envio
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {ACTION_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 text-[13px] text-n-700"
                      >
                        <input
                          type="radio"
                          name="action_after"
                          checked={actionAfter === opt.value}
                          onChange={() => setActionAfter(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {actionAfter === 'no_ticket' && (
                    <p className="mt-2 text-[12px] text-n-500">
                      O envio não cria atendimento — a mensagem fica no histórico do contato. Vira
                      um atendimento (pendente) só se a pessoa responder. Não infla a lista de
                      Fechados.
                    </p>
                  )}
                  {actionAfter === 'queue' && (
                    <div className="mt-2">
                      <label className="mb-1 block text-[11px] text-n-500">Departamento</label>
                      <select
                        value={targetDeptId}
                        onChange={(e) =>
                          setTargetDeptId(e.target.value ? Number(e.target.value) : '')
                        }
                        className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-3 text-sm sm:w-72"
                      >
                        <option value="">Selecione…</option>
                        {(deptsQ.data ?? []).map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {actionAfter === 'assign_user' && (
                    <div className="mt-2">
                      <label className="mb-1 block text-[11px] text-n-500">Usuário</label>
                      <select
                        value={targetUserId}
                        onChange={(e) =>
                          setTargetUserId(e.target.value ? Number(e.target.value) : '')
                        }
                        className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-3 text-sm sm:w-72"
                      >
                        <option value="">Selecione…</option>
                        {(usersQ.data ?? [])
                          .filter((u) => u.status === 'active')
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.email})
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Modo Cloud API → Template HSM (substitui mensagem livre).
                Mantém seção de Arquivo (pode servir como header de mídia do template). */}
                {isCloudApi ? (
                  <div className="mt-4">
                    <TemplateBuilder
                      templates={channelTemplates}
                      isLoading={templatesQ.isLoading}
                      selectedTemplateId={templateId}
                      onSelectTemplate={(id) => {
                        setTemplateId(id);
                        // Reseta variáveis ao trocar template (slots diferentes)
                        setTemplateVariables({});
                      }}
                      variables={templateVariables}
                      onVariablesChange={setTemplateVariables}
                      jsonPaths={jsonPaths}
                      hasLeadMediaPath={!!leadMediaPath}
                    />
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-[12px] text-n-600">Mensagem</label>
                      {jsonPaths.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) insertVariable(e.target.value);
                            e.target.value = '';
                          }}
                          className="h-7 rounded border border-n-300 bg-n-0 px-2 text-[11px]"
                        >
                          <option value="">Inserir variável…</option>
                          {jsonPaths.map((path) => (
                            <option key={path} value={path}>
                              {path}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <textarea
                      ref={messageRef}
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      placeholder="Digite sua mensagem…"
                      className="h-32 w-full rounded-md border border-n-300 bg-n-0 p-3 text-[13px] text-n-900 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25"
                    />
                    <p className="mt-1 text-[11px] text-n-500">
                      Use <code className="rounded bg-n-100 px-1">{'{{phone}}'}</code> ou{' '}
                      <code className="rounded bg-n-100 px-1">{'{{contact.name}}'}</code> pra
                      inserir valores do payload recebido.
                      {leadMediaPath && (
                        <>
                          {' '}
                          Com arquivo configurado, a mensagem vira <strong>legenda</strong>{' '}
                          (opcional).
                        </>
                      )}
                    </p>
                  </div>
                )}
              </section>

              {/* ───── Dados do Lead ───── */}
              <section className="mt-4 rounded-md border border-n-200 p-4">
                <div className="mb-3 font-semibold text-[13px] text-n-700">Dados do Lead</div>
                {jsonPaths.length === 0 ? (
                  <p className="text-[12px] text-n-500">
                    Preencha o <strong>Modelo de Dados</strong> acima com um exemplo de JSON pra
                    escolher quais campos correspondem ao telefone e nome do lead.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-[12px] text-n-600">
                          Telefone <span className="text-danger-500">*</span>
                        </label>
                        <PathSelect
                          value={leadPhonePath}
                          onChange={setLeadPhonePath}
                          options={jsonPaths}
                          placeholder="Selecione…"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[12px] text-n-600">Nome (opcional)</label>
                        <PathSelect
                          value={leadNamePath}
                          onChange={setLeadNamePath}
                          options={jsonPaths}
                          placeholder="Nenhum"
                          allowEmpty
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[12px] text-n-600">
                          URL do arquivo (opcional)
                        </label>
                        <PathSelect
                          value={leadMediaPath}
                          onChange={setLeadMediaPath}
                          options={jsonPaths}
                          placeholder="Nenhum"
                          allowEmpty
                        />
                      </div>
                    </div>
                    {leadMediaPath && (
                      <p className="mt-2 text-[11px] text-n-500">
                        ChatPlus baixa a URL e envia como imagem/áudio/vídeo/documento
                        (auto-detectado pelo MIME).
                      </p>
                    )}
                  </>
                )}
              </section>
            </>
          )}

          {error && (
            <div className="mt-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-[12px] text-danger-700">
              {error}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end border-t border-n-200 px-6 py-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[13px]"
            >
              Cancelar
            </button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="animate-spin" />
              ) : isEdit ? (
                'Salvar'
              ) : (
                'Criar evento'
              )}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Select de paths JSON extraídos do Modelo de Dados.
 * Garante que o usuário só escolhe campos que existem no payload de exemplo,
 * evitando typo (ex: digitar "phone" quando o payload tem "telefone").
 *
 * Se `value` atual não está em `options` (ex: edição de evento cujo Modelo de
 * Dados foi alterado depois), preserva e mostra como "(não encontrado)" pra
 * o usuário saber que precisa re-selecionar.
 */
function PathSelect({
  value,
  onChange,
  options,
  placeholder,
  allowEmpty,
}: {
  value: string;
  onChange: (next: string) => void;
  options: string[];
  placeholder?: string;
  allowEmpty?: boolean;
}) {
  const valueMissing = value && !options.includes(value);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-3 text-sm text-n-900 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25"
    >
      <option value="">{placeholder ?? 'Selecione…'}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
      {valueMissing && <option value={value}>{value} (não encontrado no Modelo de Dados)</option>}
      {allowEmpty && value && <option value="">— Nenhum —</option>}
    </select>
  );
}

function Field({
  label,
  value,
  onCopy,
  copied,
  mono,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[12px] text-n-600">
        <span className="font-semibold">{label}</span>
        <button
          type="button"
          onClick={onCopy}
          className="flex items-center gap-1 text-brand-600 hover:underline"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          Copiar
        </button>
      </div>
      <div
        className={`break-all rounded border border-n-200 bg-n-50 p-3 text-[12px] text-n-700 ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}
